import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconsModule } from '@zikata-frontend/ui';

interface NavItem {
  id: number;
  iconName: string;
  caption: string;
}

@Component({
  selector: 'zikatagov-mobile-bottom-nav',
  standalone: true,
  imports: [CommonModule, IconsModule],
  template: `
    <!-- Fixed bottom bar -->
    <nav class="bottom-nav">
      <div class="bottom-nav-inner">
        <button
          *ngFor="let item of primaryItems"
          class="nav-btn"
          [class.active]="item.id === activeItemId"
          (click)="onItemClick(item.id)"
          [attr.aria-label]="item.caption"
        >
          <span class="active-bar" *ngIf="item.id === activeItemId"></span>
          <ng-icon [name]="item.iconName" size="22"></ng-icon>
          <span class="nav-label">{{ item.caption }}</span>
        </button>

      <button
  class="nav-btn"
  [class.active]="isMoreOpen"
  (click)="toggleMore()"
  aria-label="More"
>
  <span class="more-icon-wrap" [class.rotated]="isMoreOpen">
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="5"  cy="12" r="1.5"/>
      <circle cx="12" cy="12" r="1.5"/>
      <circle cx="19" cy="12" r="1.5"/>
    </svg>
  </span>
  <span class="nav-label">More</span>
</button>
      </div>
    </nav>

    <!-- Backdrop -->
    <div class="backdrop" *ngIf="isMoreOpen" (click)="closeMore()"></div>

    <!-- More Drawer with swipe-down-to-close -->
    <div
      #drawer
      class="drawer"
      [class.drawer-open]="isMoreOpen"
      (touchstart)="touchStart($event)"
      (touchmove)="touchMove($event)"
      (touchend)="touchEnd()"
    >
      <!-- Handle bar — tap to close -->
      <div class="handle-row" (click)="closeMore()">
        <div class="handle"></div>
      </div>

      <p class="drawer-title">More Options</p>

      <div class="drawer-grid">
        <button
          *ngFor="let item of secondaryItems"
          class="drawer-item"
          [class.drawer-item-active]="item.id === activeItemId"
          (click)="onSecondaryItemClick(item.id)"
        >
          <span class="drawer-icon">
            <ng-icon [name]="item.iconName" size="20"></ng-icon>
          </span>
          <span class="drawer-label">{{ item.caption }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    :host { font-family: 'DM Sans', 'Segoe UI', sans-serif; }

    /* ─── Bottom bar ─── */
    .bottom-nav {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 1050;
      background: #fff;
      border-top: 1.5px solid #f0eafa;
      box-shadow: 0 -4px 24px rgba(109,40,217,.10);
      padding-bottom: env(safe-area-inset-bottom, 0);
    }

    .bottom-nav-inner {
      display: flex;
      height: 60px;
      max-width: 600px;
      margin: 0 auto;
      padding: 0 .25rem;
    }

    .nav-btn {
      flex: 1;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 2px;
      background: none; border: none;
      cursor: pointer; position: relative;
      padding: 6px 4px 4px;
      border-radius: 12px;
      color: #9ca3af;
      transition: color .2s;
      -webkit-tap-highlight-color: transparent;
    }
    .nav-btn:active { transform: scale(.92); }
    .nav-btn.active  { color: #7c3aed; }

    .active-bar {
      position: absolute;
      top: 0; left: 50%;
      transform: translateX(-50%);
      width: 32px; height: 3px;
      border-radius: 0 0 3px 3px;
      background: linear-gradient(90deg, #7c3aed, #a78bfa);
    }

    .nav-label {
      font-size: .6rem; font-weight: 500;
      line-height: 1; white-space: nowrap;
    }

    .more-icon-wrap { display: flex; transition: transform .25s; }
    .more-icon-wrap.rotated { transform: rotate(90deg); }

    /* ─── Backdrop ─── */
    .backdrop {
      position: fixed; inset: 0;
      background: rgba(20,5,40,.4);
      backdrop-filter: blur(3px);
      z-index: 1048;
      animation: fadein .2s ease;
    }
    @keyframes fadein { from { opacity: 0; } to { opacity: 1; } }

    /* ─── Drawer ─── */
    .drawer {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 1049;
      background: #fff;
      border-radius: 20px 20px 0 0;
      padding: 0 1rem calc(80px + env(safe-area-inset-bottom, 0));
      box-shadow: 0 -8px 40px rgba(109,40,217,.14);
      transform: translateY(100%);
      /* transition applied only when NOT dragging (see TS) */
      transition: transform .35s cubic-bezier(.4,0,.2,1);
      will-change: transform;
      touch-action: none;
    }
    .drawer-open { transform: translateY(0) !important; }

    /* Handle */
    .handle-row {
      display: flex; justify-content: center;
      padding: .75rem 0 .25rem;
      cursor: pointer;
    }
    .handle {
      width: 40px; height: 4px;
      border-radius: 99px;
      background: #d1d5db;
    }

    .drawer-title {
      font-size: .875rem; font-weight: 600;
      color: #374151; margin: .5rem 0 1rem;
      padding: 0 .25rem;
    }

    .drawer-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: .625rem;
    }

    .drawer-item {
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: .5rem;
      background: #faf8ff;
      border: 1.5px solid #f0eafa;
      border-radius: 14px;
      padding: 1rem .5rem;
      cursor: pointer; color: #6b7280;
      transition: all .2s;
      -webkit-tap-highlight-color: transparent;
    }
    .drawer-item:active,
    .drawer-item-active,
    .drawer-item:hover {
      background: #f3eeff; border-color: #c4b5fd; color: #7c3aed;
    }

    .drawer-icon {
      display: flex; align-items: center; justify-content: center;
      width: 44px; height: 44px;
      border-radius: 12px;
      background: #fff; border: 1.5px solid #ede9fb;
    }
    .drawer-item-active .drawer-icon { background: #ede9fb; border-color: #c4b5fd; }

    .drawer-label {
      font-size: .75rem; font-weight: 500;
      text-align: center; line-height: 1.2;
    }
  `],
})
export class MobileBottomNavComponent implements OnInit, OnDestroy {
  @Output() menuItemClick = new EventEmitter<number>();
  @ViewChild('drawer') drawerEl!: ElementRef<HTMLElement>;

  activeItemId: number | null = null;
  isMoreOpen = false;

  private _touchStartY = 0;
  private _dragY = 0;
  private _dragging = false;

  primaryItems: NavItem[] = [
    { id: 116, iconName: 'saxGraphOutline',               caption: 'Summary'      },
    { id: 0,   iconName: 'saxHome2Outline',               caption: 'Home'         },
    { id: 11,  iconName: 'saxSetting5Outline',            caption: 'Bulk SMS'     },
    { id: 10,  iconName: 'saxProfile2userOutline',        caption: 'Contacts'     },
    { id: 5,   iconName: 'saxArrowSwapHorizontalOutline', caption: 'Transactions' },
  ];

  secondaryItems: NavItem[] = [
    { id: 6,  iconName: 'saxProfile2userOutline', caption: 'Users'        },
    { id: 4,  iconName: 'saxCodeOutline',         caption: 'Developer'    },
    { id: 3,  iconName: 'saxUserOutline',         caption: 'Applications' },
    { id: 9,  iconName: 'saxNotificationOutline', caption: 'Alerts'       },
    { id: 12, iconName: 'saxSendOutline',         caption: 'Surveys'      },
  ];

  ngOnInit() {}
  ngOnDestroy() {}

  /* ── Swipe to close ──────────────────────────── */
  touchStart(e: TouchEvent) {
    if (!this.isMoreOpen) return;
    this._touchStartY = e.touches[0].clientY;
    this._dragY = 0;
    this._dragging = true;
    const el = this.drawerEl?.nativeElement;
    if (el) {
      // Freeze CSS transition while user is dragging
      el.style.transition = 'none';
    }
  }

  touchMove(e: TouchEvent) {
    if (!this._dragging) return;
    const delta = e.touches[0].clientY - this._touchStartY;
    if (delta <= 0) return;           // don't allow pulling UP
    this._dragY = delta;
    const el = this.drawerEl?.nativeElement;
    if (el) el.style.transform = `translateY(${delta}px)`;
  }

  touchEnd() {
    if (!this._dragging) return;
    this._dragging = false;
    const el = this.drawerEl?.nativeElement;
    // Re-enable CSS transition
    if (el) el.style.transition = '';

    if (this._dragY > 90) {
      // Enough swipe → close
      this.closeMore();
    } else {
      // Snap back to open position
      if (el) el.style.transform = 'translateY(0)';
    }
    this._dragY = 0;
  }

  /* ── Nav actions ─────────────────────────────── */
  onItemClick(id: number) {
    this.activeItemId = id;
    this.menuItemClick.emit(id);
  }

  onSecondaryItemClick(id: number) {
    this.activeItemId = id;
    this.menuItemClick.emit(id);
    this.closeMore();
  }

  toggleMore() { this.isMoreOpen = !this.isMoreOpen; }

  closeMore() {
    this.isMoreOpen = false;
    const el = this.drawerEl?.nativeElement;
    if (el) el.style.transform = '';
  }
}